import { useChat } from "@ai-sdk/react";
import { lastAssistantMessageIsCompleteWithToolCalls } from "ai";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { displayToast } from "../../components/app/AppToast";
import { refetchApiQuery } from "../../services/api";
import { useEffectOnce } from "../app/hooks/useEffectOnce";
import { useFirstTokenHaptic } from "../gpt/hooks/useFirstTokenHaptic";
import {
  assistantUIMessages,
  uiMessageHasAnswer,
  uiMessageRequest,
  type GeneralChatUIMessage,
} from "../gpt/hooks/useGptChat";
import {
  createAssistantChatTransport,
  type AssistantOrigin,
} from "./assistantChatTransport";
import {
  withServerSummary,
  type Redaction,
  type RedactionNotice,
} from "./redaction";
import { usePatientDataInputRuleSeen } from "../gpt/stores/patientDataInputRuleStore";
import { gaEventGptPatientDataRedacted } from "../../tools/analytics/domains/gpt.analytics";

// What a retry replays. Every request is idempotent on its ids.
type Attempt =
  | { kind: "send"; origin: AssistantOrigin }
  | { kind: "regenerate"; runId: string; requestId: string }
  | { kind: "answer" }
  | { kind: "resume" };

// The history leaves an active run's message to the stream, so its question comes last.
// A question copied from a legacy Search turn comes last too, with no answer and no run.
function activeRunId(messages: GeneralChatUIMessage[]): string | null {
  const last = messages.at(-1);
  if (last === undefined || last.role !== "user") return null;
  if (last.metadata === undefined)
    throw new Error("should not happen: a question without its metadata");
  return last.metadata.runId;
}

// `useChat` on the assistant engine, with the commands its UI sends besides messages.
export function useAssistantChat(input: {
  conversationId: string;
  initialMessages: GeneralChatUIMessage[];
  // False while a question awaits its first send, which opens its own stream.
  // True resyncs the history at mount, then resumes its active run.
  resume: boolean;
  onTurnAccepted: () => void;
  // The question moved to a new conversation, with the Notice the reader sees.
  onTurnMoved: (conversationId: string, notice: RedactionNotice | null) => void;
  onTurnFinish: () => void;
  // The server could not check the last send for patient data and stored nothing (AD-20).
  // Captures that send at once, and returns the call that shows it as unsent.
  onRedactionRefused: () => () => void;
  // The Notice of a send that moved here from another conversation.
  initialNotice: RedactionNotice | null;
}) {
  const { t } = useTranslation();
  const conversationId = input.conversationId;
  // Latest-value ref: the transport lives as long as the conversation.
  const onTurnAccepted = useRef(input.onTurnAccepted);
  onTurnAccepted.current = input.onTurnAccepted;
  const onTurnMoved = useRef(input.onTurnMoved);
  onTurnMoved.current = input.onTurnMoved;
  const onRedactionRefused = useRef(input.onRedactionRefused);
  onRedactionRefused.current = input.onRedactionRefused;
  // The Notice of the last send, live only: a reload shows the Markers without it (AD-18).
  const [notice, setNotice] = useState<RedactionNotice | null>(
    input.initialNotice
  );
  // Latest-value ref: the banner follows the reader to the new conversation.
  const shownNotice = useRef(notice);
  shownNotice.current = notice;
  // Set once `chat` exists: the transport is created before it.
  const showServerText = useRef<(text: string) => void>(() => {
    throw new Error("should not happen: a send before the chat exists");
  });
  const assistant = useMemo(
    () =>
      createAssistantChatTransport({
        conversationId,
        onAccepted: (redaction) => {
          // The server pass found what the device missed (AD-20).
          if (redaction !== null && redaction.summary !== null) {
            const merged = withServerSummary(
              shownNotice.current,
              redaction.summary
            );
            // A move reads the ref before the next render.
            shownNotice.current = merged;
            setNotice(merged);
            showServerText.current(redaction.text);
          }
          onTurnAccepted.current();
        },
        onMoved: (movedTo) => {
          onTurnMoved.current(movedTo, shownNotice.current);
        },
      }),
    [conversationId]
  );
  // Only the mount can resume before a hook call records its request.
  const attempt = useRef<Attempt>({ kind: "resume" });
  // Set by a failed run, which a retry regenerates instead of replaying.
  const failedRunId = useRef<string | null>(null);
  // Set by a send that the server could not check, until its error is cleared.
  const refusedSend = useRef(false);
  const onTurnFinish = input.onTurnFinish;
  const [inputRuleSeen, markInputRuleSeen] = usePatientDataInputRuleSeen();
  // Every Question leaves through `send` or `answer`: the first one retires the
  // Input rule, and each Redaction is counted once (REQ-08).
  function notify(redaction: Redaction) {
    if (!inputRuleSeen) markInputRuleSeen();
    // A send without a Match clears the previous Notice.
    setNotice(redaction.notice);
    if (redaction.notice === null) return;
    gaEventGptPatientDataRedacted(redaction.notice.summary);
  }
  const chat = useChat<GeneralChatUIMessage>({
    id: conversationId,
    transport: assistant.transport,
    messages: input.initialMessages,
    // The user message id is the run id, so it must be a UUID.
    generateId: () => crypto.randomUUID(),
    // An answered card resumes its run; the stream's `start-step` ends that step.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onFinish: ({ isError }) => {
      if (isError) {
        // A refused send shows as an unsent message, not as a failed turn.
        if (refusedSend.current) {
          refusedSend.current = false;
          chat.clearError();
        }
        return;
      }
      onTurnFinish();
    },
    onError: () => {
      failedRunId.current = assistant.failedRunId();
      if (assistant.redactionRefused()) {
        // The server stored nothing: the question leaves the transcript for an
        // unsent message, and an answered card goes back to pending.
        if (attempt.current.kind === "send")
          chat.setMessages((messages) => {
            const question = messages.findLastIndex((m) => m.role === "user");
            return question === -1 ? messages : messages.slice(0, question);
          });
        // The SDK sets the error after `onError`: `onFinish` clears it.
        refusedSend.current = true;
        // Captured now: a newer send must not take its place.
        const show = onRedactionRefused.current();
        // An answered card goes back to pending first, so a Retry answers it.
        // The unsent message shows even when the history read fails.
        if (attempt.current.kind === "answer") void resync().finally(show);
        else show();
        return;
      }
      // The card shows an answer the server may not hold, and the composer would
      // then ask a new question of a waiting run. The history says which holds.
      if (attempt.current.kind === "answer" && failedRunId.current === null)
        void resync();
    },
  });

  // The question bubble shows what the server stored. A card answer keeps its text until the next resync.
  showServerText.current = (text) => {
    if (attempt.current.kind !== "send") return;
    chat.setMessages((messages) => {
      const question = messages.findLastIndex((m) => m.role === "user");
      if (question === -1)
        throw new Error(
          "should not happen: an accepted send without its question"
        );
      return messages.map((message, index) =>
        index === question
          ? {
              ...message,
              parts: message.parts.map((part) =>
                part.type === "text" ? { ...part, text } : part
              ),
            }
          : message
      );
    });
  };

  const last = chat.messages.at(-1);
  useFirstTokenHaptic(
    chat.status === "streaming" &&
      last !== undefined &&
      last.role === "assistant" &&
      uiMessageHasAnswer(last)
  );

  // The submit is idempotent on the user message id, which is `requestId`.
  function send(
    requestId: string,
    redaction: Redaction,
    origin: AssistantOrigin
  ) {
    attempt.current = { kind: "send", origin };
    notify(redaction);
    void chat.sendMessage(
      { id: requestId, parts: [{ type: "text", text: redaction.text }] },
      { body: { origin } }
    );
  }

  function regenerate(runId: string) {
    const next = {
      kind: "regenerate",
      runId,
      requestId: crypto.randomUUID(),
    } as const;
    attempt.current = next;
    void chat.regenerate({
      body: { runId: next.runId, requestId: next.requestId },
    });
  }

  // A resumed run's current message is missing from the history, so the
  // stream rebuilds it whole.
  async function resync() {
    const started = attempt.current;
    const history = await refetchApiQuery(
      "/assistant/conversations/:conversationId",
      { param: { conversationId } }
    ).then(
      (body) => body,
      () => null
    );
    if (history === null) {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    // A send during the read owns the transcript now.
    if (attempt.current !== started) return;
    const messages = assistantUIMessages(history.messages);
    chat.setMessages(messages);
    chat.clearError();
    attempt.current = { kind: "resume" };
    const runId = activeRunId(messages);
    if (runId === null) return;
    await chat.resumeStream({ body: { runId } });
  }

  // The seed may be a cached read that misses the latest turns.
  useEffectOnce(() => {
    if (!input.resume) return;
    void resync();
  });

  async function retry() {
    const runId = failedRunId.current;
    failedRunId.current = null;
    if (runId !== null) {
      regenerate(runId);
      return;
    }
    const current = attempt.current;
    switch (current.kind) {
      case "send": {
        // The replayed stream rebuilds the partial answer from its first chunk.
        const question = chat.messages
          .flatMap((message, index) => (message.role === "user" ? [index] : []))
          .at(-1);
        if (question === undefined)
          throw new Error("should not happen: a send without its question");
        chat.setMessages(chat.messages.slice(0, question + 1));
        void chat.sendMessage(undefined, { body: { origin: current.origin } });
        return;
      }
      case "regenerate":
        void chat.regenerate({
          body: { runId: current.runId, requestId: current.requestId },
        });
        return;
      case "answer":
      case "resume":
        await resync();
        return;
      default:
        throw new Error(current satisfies never);
    }
  }

  // The SDK sends the answer. A refused or broken answer resyncs the history,
  // which holds the card pending or answered.
  function answer(toolCallId: string, redaction: Redaction) {
    attempt.current = { kind: "answer" };
    notify(redaction);
    void chat.addToolOutput({
      tool: "askUser",
      toolCallId,
      output: { answer: redaction.text },
    });
  }

  // `chat.stop` aborts only a stream it opened; the transport stops the run.
  async function stop() {
    // A card that reached the reader before the stop asks nothing anymore.
    const asked =
      last !== undefined &&
      last.role === "assistant" &&
      uiMessageRequest(last) !== null;
    await chat.stop();
    await assistant.stop();
    if (asked) await resync();
  }

  return {
    chat,
    notice,
    dismissNotice: () => {
      setNotice(null);
    },
    send,
    regenerate,
    retry,
    stop,
    answer,
  };
}
